/**
 * Step2Income — Phase 27 (was "Step 3" pre-refactor).
 *
 * Branches by deal type:
 *   • flip    → ARV, repair budget, holding period, holding costs, selling costs, cash-purchase
 *   • brrrr   → ARV, repair budget, stabilisation period, holding costs, stabilised rent
 *   • rental  → Monthly rent + MarketSuggestion
 *   • multifamily → MultifamilyUnitMixGrid (Phase 13 specialised calculator)
 *
 * Verbatim port from DealWizard.jsx renderStep step === 3 branch.
 * Preserve as-is: the holding_period_months ?? 6 / stabilization_months ?? 6
 * defaults are intentional — the engine treats null differently from 0.
 */
import { Label } from "@/components/ui/label";
import MarketSuggestion from "@/components/market/MarketSuggestion";
import MultifamilyUnitMixGrid from "@/components/deal/MultifamilyUnitMixGrid";
import { NumberField } from "./_shared";
// Phase 47.3 — ARV PSF/PSM chip beneath the ARV input on flip/BRRRR.
import { formatPsfPsm } from "@/lib/priceNormalisation";

export default function Step2Income({ wizard }) {
  const { form, errors, dealType, setAnalysis, setForm } = wizard;
  const isFlip = dealType === "flip";
  const isBRRRR = dealType === "brrrr";
  const isMF = dealType === "multifamily";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">
          {isFlip ? "Resale & Rehab" : "Income"}
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          {isFlip
            ? "ARV, repair budget, holding period — the levers a flip lives or dies on."
            : isBRRRR
            ? "Stabilised rent + the rehab + refi assumptions."
            : "What does this property bring in?"}
        </p>
      </div>

      {(isFlip || isBRRRR) ? (
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <NumberField id="arv" label="ARV (after-repair value)" prefix="$"
              value={form.analysis.arv}
              onChange={(v) => setAnalysis("arv", v)}
              testid="analysis-arv"
              hint={isFlip ? "What you'll sell it for after renovation." : "Appraisal target post-rehab — drives the refi loan."} />
            {isFlip && errors.arv ? (
              <p data-testid="error-arv" className="mt-1 text-xs text-red-600">{errors.arv}</p>
            ) : null}
            {/* Phase 47.3 — ARV PSF/PSM chip. "ARV $X/sqft" lets the
                flipper/BRRRR investor sanity-check the post-rehab
                value against neighbourhood medians at-a-glance. */}
            {Number(form.analysis.arv) > 0 && Number(form.property.square_feet) >= 50 ? (
              <div
                data-testid="wizard-arv-psf-psm"
                className="mt-1 text-[11px] tabular-nums text-slate-500"
              >
                ARV ~{formatPsfPsm(form.analysis.arv, form.property.square_feet)}
              </div>
            ) : null}
            {isFlip ? (
              <div className="mt-2">
                <Label htmlFor="arv_source" className="text-xs text-slate-500">
                  ARV source
                </Label>
                <select
                  id="arv_source"
                  data-testid="analysis-arv-source"
                  value={form.analysis.arv_source || "unverified"}
                  onChange={(e) => setAnalysis("arv_source", e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="verified">Verified (broker comps / appraisal)</option>
                  <option value="estimated">Estimated (AVM / rough comps)</option>
                  <option value="unverified">Unverified (guess)</option>
                </select>
              </div>
            ) : null}
          </div>
          <NumberField id="repair_budget" label="Repair / rehab budget" prefix="$"
            value={form.analysis.repair_budget}
            onChange={(v) => setAnalysis("repair_budget", v)}
            testid="analysis-repair-budget"
            hint="Include 10-20% contingency." />
          <NumberField id="hold_period" label={isFlip ? "Holding period" : "Stabilisation period"} suffix="mo"
            value={isFlip ? (form.analysis.holding_period_months ?? 6) : (form.analysis.stabilization_months ?? 6)}
            onChange={(v) => setAnalysis(isFlip ? "holding_period_months" : "stabilization_months", v)}
            testid="analysis-hold-months"
            hint={isFlip ? "How many months from close to sale." : "Rehab + lease-up before refinance."} />
          <NumberField id="hold_costs_m" label="Holding costs" prefix="$" suffix="/mo"
            value={form.analysis.holding_costs_monthly ?? 0}
            onChange={(v) => setAnalysis("holding_costs_monthly", v)}
            testid="analysis-holding-costs"
            hint="Utilities + insurance + HOA during the rehab/hold." />
          {isFlip ? (
            <>
              <NumberField id="selling_costs" label="Selling costs" suffix="%"
                value={form.analysis.selling_costs_pct ?? 7}
                onChange={(v) => setAnalysis("selling_costs_pct", v)}
                testid="analysis-selling-costs"
                hint="Agent + closing — typically 6-9% (default 7%)." />
              <div className="flex items-center gap-2 pt-7">
                <input type="checkbox" id="cash_purchase"
                  checked={!!form.analysis.cash_purchase}
                  onChange={(e) => setAnalysis("cash_purchase", e.target.checked)}
                  data-testid="analysis-cash-purchase"
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                <Label htmlFor="cash_purchase" className="cursor-pointer text-sm text-slate-700">
                  All-cash purchase (skip loan)
                </Label>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {isMF ? (
        <MultifamilyUnitMixGrid
          value={form.unit_mix || []}
          onChange={(rows) => setForm((f) => ({ ...f, unit_mix: rows }))}
          buildingPrice={Number(form.property.asking_price) || 0}
          buildingSqft={Number(form.property.square_feet) || 0}
        />
      ) : null}
      {isMF && errors.unit_mix ? (
        <p className="text-xs text-red-600">{errors.unit_mix}</p>
      ) : null}

      {!isFlip && !isMF ? (
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <NumberField id="rent" label={isBRRRR ? "Stabilised monthly rent" : "Monthly rent"} prefix="$" suffix="/mo"
              value={form.analysis.monthly_rent}
              onChange={(v) => setAnalysis("monthly_rent", v)}
              testid="analysis-monthly-rent" />
            <MarketSuggestion
              kind="rent"
              address={form.property.address}
              city={form.property.city}
              state={form.property.state}
              zip={form.property.zip}
              country={form.property.country}
              beds={Number(form.property.bedrooms) || undefined}
              baths={Number(form.property.bathrooms) || undefined}
              sqft={Number(form.property.square_feet) || undefined}
              propertyType={form.property.property_type}
              onAccept={(v) => setAnalysis("monthly_rent", v)}
              testidPrefix="wizard-market-rent"
            />
          </div>
        </div>
      ) : null}
      {errors.monthly_rent && <p className="text-xs text-red-600">{errors.monthly_rent}</p>}
    </div>
  );
}
